import { QueryKey, useQuery } from '@tanstack/react-query';
import type { CancelToken } from 'axios';
import { toCancelToken } from 'src/api/queries/queryClient';

const DOCUMENT_OPTIONS_STALE_TIME = 60_000;

export interface IWorkflowModuleQueryConfig<TDto, TOption> {
  /** Lower-case noun used in the error-pipeline log context, e.g. "change". */
  label: string;
  listKey: (jobId: number) => QueryKey;
  detailKey: (jobId: number, id: string) => QueryKey;
  documentOptionsKey: (jobId: number) => QueryKey;
  list: (jobId: number, cancelToken?: CancelToken) => Promise<TDto[]>;
  detail: (
    jobId: number,
    id: string,
    cancelToken?: CancelToken,
  ) => Promise<TDto>;
  documentOptions: (
    jobId: number,
    cancelToken?: CancelToken,
  ) => Promise<TOption[]>;
}

/**
 * Builds the read hooks shared by the five workflow record modules (changes,
 * RFIs, submittals, drawings, transmittals), which differ only in their
 * generated client method names and query keys.
 */
export function createModuleQueries<TDto, TOption>(
  config: IWorkflowModuleQueryConfig<TDto, TOption>,
) {
  const useList = (jobId: number | null | undefined) =>
    useQuery({
      queryKey: config.listKey(jobId ?? -1),
      queryFn: ({ signal }) => config.list(jobId!, toCancelToken(signal)),
      enabled: jobId != null && jobId > 0,
      meta: { errorContext: `${config.label} list` },
    });

  const useDetail = (
    jobId: number | null | undefined,
    id: string | null | undefined,
  ) =>
    useQuery({
      queryKey: config.detailKey(jobId ?? -1, id ?? ''),
      queryFn: ({ signal }) =>
        config.detail(jobId!, id!, toCancelToken(signal)),
      enabled: jobId != null && jobId > 0 && !!id,
      staleTime: 0,
      meta: { errorContext: `${config.label} detail` },
    });

  const useDocumentOptions = (jobId: number | null | undefined) =>
    useQuery({
      queryKey: config.documentOptionsKey(jobId ?? -1),
      queryFn: ({ signal }) =>
        config.documentOptions(jobId!, toCancelToken(signal)),
      enabled: jobId != null && jobId > 0,
      staleTime: DOCUMENT_OPTIONS_STALE_TIME,
      meta: { errorContext: `${config.label} document options` },
    });

  return { useList, useDetail, useDocumentOptions };
}
